/**
 * Trofeos por nivel MCER — especificación de Santiago (Alianza Francesa)
 * ----------------------------------------------------------------------
 * Fuente: Excel "Sistema_de_recompensas_completo_3.xlsx" (reunión 18/08/2026,
 * completado 24-25/08). Por nivel: 7 trofeos de bronce + un 8.º de plata que
 * se desbloquea al obtener los otros 7 (la hoja C trae 10 bronce + 1 plata y
 * sirve para C1 y C2). La recompensa de cada bronce es una pieza del paisaje
 * (mapeo por orden en galiaScenes.trophyIdForPiece).
 *
 * El reconocimiento conversacional lo hace el LLM en galia-ws: el prompt V3
 * (buildTropheesPromptBlock) instruye a Galia a emitir [[trophee:ID:CASILLA]]
 * cuando el ÚLTIMO mensaje del estudiante cumple un objetivo. El servidor los
 * extrae (nunca llegan al chat ni al TTS) y los reenvía como __TROPHEES__.
 *
 * Los trofeos ACADÉMICOS no pasan por el LLM: galia-ws los evalúa contra los
 * datos oficiales AEC (notas CO/CE/PO/PE, asistencia, nota total) al inicio de
 * la sesión y los manda por el mismo canal, con la casilla = id del curso
 * (`dynamic`: cuenta cursos distintos, así re-enviarlos cada sesión no infla).
 *
 * Ajustes de calibración acordados con Santiago (25/08, WhatsApp):
 *  - A1 Localiser: 4 casillas (à+ville, en/au/aux+pays); la casilla la decide
 *    la preposición LITERAL del estudiante (refineRecognitions).
 *  - A2 partitivos: 2 usos por partitivo (el Excel pedía 5).
 *  - B1 viaje: el relato puede acumularse a lo largo de una conversación.
 *  - B2 "10 relatos diferentes" → frases correctas en varias conversaciones.
 *
 * PENDIENTE (insumo externo): trofeos culturales (Excel de mediateca vía
 * Mauricio) y DELF (listas de la líder de exámenes) — entrarán por carga de
 * archivo al dashboard cuando existan los archivos.
 */

import { MCERLevel } from '../types';
import { isKioskMode } from '../utils/featureFlags';

export interface LevelTrophy {
  /** Id estable (clave de persistencia y del marcador LLM). */
  id: string;
  /** Nivel MCER al que pertenece ('AEC' para los académicos). */
  level: MCERLevel | 'AEC';
  emoji: string;
  /** Nombre del trofeo (Excel, francés) — se muestra grande. */
  titleFr: string;
  /** Nombre de apoyo en español (también se usa en el checklist del prompt). */
  title: string;
  /** Descripción al desbloquear (Excel, francés). */
  descFr: string;
  /** Frase corta al desbloquear (español, para el toast). */
  blurb: string;
  /** Pista de cómo lograrlo (español) — se muestra bloqueado. */
  hint: string;
  /**
   * Casillas de reconocimiento: id de casilla → veces requeridas.
   * El marcador del LLM es [[trophee:<id>:<casilla>]].
   * Un trofeo se desbloquea cuando TODAS sus casillas llegan a su meta.
   */
  counters: Record<string, number>;
  /**
   * Casillas DINÁMICAS (trofeos académicos): se acepta cualquier casilla (una
   * por curso AEC, tope 1 c/u) y el trofeo se desbloquea con `dynamic`
   * casillas distintas llenas. `counters` se ignora.
   */
  dynamic?: number;
  /** Etiqueta corta por casilla (español) para la vista de progreso. */
  counterLabels?: Record<string, string>;
  /** true → trofeo de plata: se desbloquea al completar los demás del nivel. */
  meta?: boolean;
  /**
   * Instrucción de reconocimiento para el prompt del LLM (español, con los
   * marcadores inline). Ausente en meta y académicos.
   */
  rule?: string;
  /**
   * true → el reconocimiento es DETERMINISTA: lo hace detectLocalRecognitions()
   * por regex sobre el texto literal del estudiante, y el trofeo se EXCLUYE del
   * prompt del LLM (si el modelo alucina su marcador igual, App.tsx lo filtra).
   *
   * Feedback de Santiago (26/08, tras 40 min de prueba A1): el LLM se saltaba
   * reconocimientos objetivos una y otra vez (être 3/6 sujetos, preguntas 7/8,
   * números lentos). Para objetivos cuya corrección ES la forma literal
   * ("nous sommes" correcto por construcción), la regex no falla nunca y no
   * gasta prompt. El LLM queda solo para lo que sí exige juicio (relatos,
   * passé composé, argumentación…).
   */
  local?: boolean;
}

/* Casillas estándar por sujeto (conjugaciones). */
const SUJETOS = ['je', 'tu', 'il_elle', 'nous', 'vous', 'ils_elles'] as const;
const porSujeto = (n: number): Record<string, number> =>
  Object.fromEntries(SUJETOS.map((s) => [s, n]));
const sujetoLabels = (prefix: string): Record<string, string> => ({
  je: `${prefix} (je)`,
  tu: `${prefix} (tu)`,
  il_elle: `${prefix} (il/elle)`,
  nous: `${prefix} (nous)`,
  vous: `${prefix} (vous)`,
  ils_elles: `${prefix} (ils/elles)`,
});
const marcadoresSujetos = (id: string) =>
  SUJETOS.map((s) => `[[trophee:${id}:${s}]]`).join(' ');

/* ══════════════════════════ A1 ══════════════════════════ */

export const A1_TROPHIES: LevelTrophy[] = [
  {
    id: 'a1_saluer',
    local: true,
    level: 'A1',
    emoji: '👋',
    titleFr: 'Wesh !',
    title: 'Saludar',
    descFr:
      'Tu as salué Galia sous toutes ses formes : formel, informel, jour ou soir. Wesh, on est déjà copains !',
    blurb: 'Saludaste a Galia de todas las formas: formal, informal, de día y de noche.',
    hint: 'Saluda a Galia en francés variando el saludo (salut, bonjour, bonsoir…)',
    counters: { fois: 5 },
    counterLabels: { fois: 'saludos' },
    rule: 'Saluda en francés (salut, bonjour, bonsoir, coucou…) → [[trophee:a1_saluer:fois]]',
  },
  {
    id: 'a1_etre',
    local: true,
    level: 'A1',
    emoji: '🎭',
    titleFr: 'Être ou ne pas être',
    title: 'El verbo être',
    descFr:
      'Tu conjugues « être » au présent sans hésiter, à toutes les personnes. La grande question est résolue !',
    blurb: 'Conjugaste « être » en presente con todos los sujetos.',
    hint: 'Usa correctamente « être » en presente con cada sujeto (je suis, tu es…)',
    counters: porSujeto(1),
    counterLabels: sujetoLabels('être'),
    rule: `Conjuga « être » en presente correctamente → según el sujeto usado: ${marcadoresSujetos('a1_etre')}`,
  },
  {
    id: 'a1_localiser',
    local: true,
    level: 'A1',
    emoji: '📍',
    titleFr: 'C’est où ça ?',
    title: 'Ubicar lugares',
    descFr:
      'Tu maîtrises les prépositions de lieu : à, au, en, aux. Plus aucune ville ni aucun pays ne peut se cacher de toi !',
    blurb: 'Usaste bien las preposiciones de lugar: à, en, au, aux.',
    hint: 'Di dónde quedan ciudades y países (à Bogotá, en France, au Canada, aux États-Unis)',
    counters: { ville_a: 1, pays_en: 1, pays_au: 1, pays_aux: 1 },
    counterLabels: {
      ville_a: 'à + ciudad',
      pays_en: 'en + país',
      pays_au: 'au + país',
      pays_aux: 'aux + país',
    },
    rule: 'Usa bien una preposición de lugar — la casilla la decide la PREPOSICIÓN LITERAL que el estudiante escribió, no el lugar: escribió « à »+ciudad ("à Bogotá") → [[trophee:a1_localiser:ville_a]] · escribió « en »+país ("en Colombie", "en France") → [[trophee:a1_localiser:pays_en]] · escribió « au »+país ("au Canada", "au Brésil") → [[trophee:a1_localiser:pays_au]] · escribió « aux »+país ("aux États-Unis") → [[trophee:a1_localiser:pays_aux]]',
  },
  {
    id: 'a1_compter',
    local: true,
    level: 'A1',
    emoji: '🔢',
    titleFr: 'Compte sur moi !',
    title: 'Contar',
    descFr: 'Tu comptes de 1 à 999 sans te tromper. On peut vraiment compter sur toi !',
    blurb: 'Dijiste números correctos en los cuatro rangos, del 1 al 999.',
    hint: 'Usa números en francés de distintos rangos (1-10, 11-69, 70-99, 100-999)',
    counters: { n1_10: 1, n11_69: 1, n70_99: 1, n100_999: 1 },
    counterLabels: {
      n1_10: '1 a 10',
      n11_69: '11 a 69',
      n70_99: '70 a 99',
      n100_999: '100 a 999',
    },
    rule: 'Usa correctamente un número en francés (escrito en letras o dicho por voz) → según el rango: 1-10: [[trophee:a1_compter:n1_10]] · 11-69: [[trophee:a1_compter:n11_69]] · 70-99: [[trophee:a1_compter:n70_99]] · 100-999: [[trophee:a1_compter:n100_999]]',
  },
  {
    id: 'a1_loisirs',
    local: true,
    level: 'A1',
    emoji: '❤️',
    titleFr: 'C’est kif-kif ?',
    title: 'Hablar de gustos',
    descFr:
      'Tu exprimes tes goûts avec « j’aime », « je n’aime pas », « j’adore » et « je déteste ». Kif ou pas kif, tu sais le dire !',
    blurb: 'Expresaste tus gustos con j’aime, je n’aime pas, j’adore y je déteste.',
    hint: 'Di qué te gusta y qué no (j’aime…, je n’aime pas…, j’adore…, je déteste…)',
    counters: { aime: 1, aime_pas: 1, adore: 1, deteste: 1 },
    counterLabels: {
      aime: 'j’aime',
      aime_pas: 'je n’aime pas',
      adore: 'j’adore',
      deteste: 'je déteste',
    },
    rule: "Expresa un gusto con actividad → j'aime: [[trophee:a1_loisirs:aime]] · je n'aime pas: [[trophee:a1_loisirs:aime_pas]] · j'adore: [[trophee:a1_loisirs:adore]] · je déteste: [[trophee:a1_loisirs:deteste]]",
  },
  {
    id: 'a1_famille',
    local: true,
    level: 'A1',
    emoji: '👨‍👩‍👧',
    titleFr: 'C’est de famille !',
    title: 'Hablar de la familia',
    descFr:
      'Tu parles de ta famille : mère, père, frères, sœurs, cousins… Toute la tribu y passe !',
    blurb: 'Hablaste de tu familia en francés.',
    hint: 'Cuéntale a Galia sobre tu familia (ma mère, mon frère…)',
    counters: { mention: 4 },
    counterLabels: { mention: 'menciones' },
    rule: 'Habla de un miembro de su familia (mère, père, frère, sœur, cousin, parents…) → [[trophee:a1_famille:mention]]',
  },
  {
    id: 'a1_questions',
    local: true,
    level: 'A1',
    emoji: '🎤',
    titleFr: 'Et alors ?',
    title: 'Hacer preguntas',
    descFr:
      'Tu poses des questions ouvertes et fermées comme un(e) vrai(e) journaliste. Et alors, la suite ?',
    blurb: 'Hiciste preguntas abiertas y cerradas como un(a) periodista.',
    hint: 'Hazle preguntas a Galia: cerradas (est-ce que…) y abiertas (pourquoi, comment…)',
    counters: { ouverte: 4, fermee: 4 },
    counterLabels: { ouverte: 'abiertas', fermee: 'cerradas' },
    rule: "Te hace una pregunta en francés correcto → cerrada (respuesta oui/non): [[trophee:a1_questions:fermee]] · abierta (pourquoi, comment, où, quand, qu'est-ce que…): [[trophee:a1_questions:ouverte]]",
  },
  {
    id: 'a1_decouverte',
    level: 'A1',
    emoji: '🧭',
    titleFr: 'La découverte',
    title: 'El descubrimiento',
    descFr:
      'Tu as obtenu tous les trophées du niveau A1. Bienvenue dans l’aventure du français !',
    blurb: '¡Obtuviste todos los trofeos del nivel A1!',
    hint: 'Desbloquea los 7 trofeos del nivel A1',
    counters: {},
    meta: true,
  },
];

/* ══════════════════════════ A2 ══════════════════════════ */

export const A2_TROPHIES: LevelTrophy[] = [
  {
    id: 'a2_pouvoir_vouloir',
    local: true,
    level: 'A2',
    emoji: '💪',
    titleFr: 'Quand on veut, on peut',
    title: 'Pouvoir y vouloir',
    descFr: 'Tu maîtrises « pouvoir » et « vouloir » à toutes les personnes : rien ne t’arrête !',
    blurb: 'Conjugaste pouvoir y vouloir con todos los sujetos.',
    hint: 'Usa pouvoir y vouloir en presente con cada sujeto (je peux, tu veux…)',
    counters: {
      pouvoir_je: 1, pouvoir_tu: 1, pouvoir_il: 1, pouvoir_nous: 1, pouvoir_vous: 1, pouvoir_ils: 1,
      vouloir_je: 1, vouloir_tu: 1, vouloir_il: 1, vouloir_nous: 1, vouloir_vous: 1, vouloir_ils: 1,
    },
    counterLabels: {
      pouvoir_je: 'je peux', pouvoir_tu: 'tu peux', pouvoir_il: 'il/elle peut',
      pouvoir_nous: 'nous pouvons', pouvoir_vous: 'vous pouvez', pouvoir_ils: 'ils/elles peuvent',
      vouloir_je: 'je veux', vouloir_tu: 'tu veux', vouloir_il: 'il/elle veut',
      vouloir_nous: 'nous voulons', vouloir_vous: 'vous voulez', vouloir_ils: 'ils/elles veulent',
    },
    rule: 'Conjuga « pouvoir » o « vouloir » en presente correctamente → verbo y sujeto usados: pouvoir: [[trophee:a2_pouvoir_vouloir:pouvoir_je]] [[trophee:a2_pouvoir_vouloir:pouvoir_tu]] [[trophee:a2_pouvoir_vouloir:pouvoir_il]] [[trophee:a2_pouvoir_vouloir:pouvoir_nous]] [[trophee:a2_pouvoir_vouloir:pouvoir_vous]] [[trophee:a2_pouvoir_vouloir:pouvoir_ils]] · vouloir: [[trophee:a2_pouvoir_vouloir:vouloir_je]] [[trophee:a2_pouvoir_vouloir:vouloir_tu]] [[trophee:a2_pouvoir_vouloir:vouloir_il]] [[trophee:a2_pouvoir_vouloir:vouloir_nous]] [[trophee:a2_pouvoir_vouloir:vouloir_vous]] [[trophee:a2_pouvoir_vouloir:vouloir_ils]]',
  },
  {
    id: 'a2_habitudes',
    level: 'A2',
    emoji: '⏰',
    titleFr: 'Métro, boulot, dodo',
    title: 'Describir hábitos',
    descFr: 'Tu as décrit ton quotidien comme un(e) vrai(e) Parisien(ne) : métro, boulot, dodo !',
    blurb: 'Describiste tus hábitos y rutinas en francés.',
    hint: 'Cuenta tus hábitos con frases completas (je me lève à…, tous les jours je…)',
    counters: { fois: 5 },
    counterLabels: { fois: 'frases de hábitos' },
    rule: 'Describe un hábito o costumbre suya con una frase completa (verbos pronominales o rutinas: "je me lève à 7h", "tous les jours je…") → [[trophee:a2_habitudes:fois]]',
  },
  {
    id: 'a2_passe_compose',
    level: 'A2',
    emoji: '⏳',
    titleFr: 'C’est du passé !',
    title: 'Contar en passé composé',
    descFr: 'Tu as raconté toute une histoire au passé composé. Une vraie machine à remonter le temps !',
    blurb: 'Contaste experiencias en passé composé.',
    hint: 'Cuenta algo que hiciste (tus vacaciones, tu fin de semana) en passé composé',
    counters: { fois: 10 },
    counterLabels: { fois: 'frases en passé composé' },
    rule: 'Conjuga correctamente un verbo en passé composé dentro de un relato (sus vacaciones, su fin de semana…) → [[trophee:a2_passe_compose:fois]] (uno por frase correcta, máximo 3 por turno)',
  },
  {
    id: 'a2_partitifs',
    level: 'A2',
    emoji: '🥖',
    titleFr: 'Encore un peu ?',
    title: 'Los partitivos',
    descFr: 'Tu utilises les articles partitifs (du, de la, de l’, des) comme un chef qui régale ses invités.',
    blurb: 'Usaste bien los artículos partitivos.',
    hint: 'Habla de comida y cantidades con du, de la, de l’, des (je mange du pain…)',
    counters: { du: 2, de_la: 2, de_l: 2, des: 2 },
    counterLabels: { du: 'du', de_la: 'de la', de_l: 'de l’', des: 'des' },
    rule: "Usa correctamente un artículo partitivo → según el partitivo LITERAL usado: du: [[trophee:a2_partitifs:du]] · de la: [[trophee:a2_partitifs:de_la]] · de l': [[trophee:a2_partitifs:de_l]] · des: [[trophee:a2_partitifs:des]]",
    // Mesa del 29/09/2026 (Santiago probando A2 en dev): dijo « du café »,
    // « des œufs », « de la farine », « de l'eau », « du sel »… y el LLM solo
    // marcó du y de la; de l' y des quedaron en 0. Pasa a regex
    // (detectPartitifSlots), como imparfait y pouvoir/vouloir.
    local: true,
  },
  {
    id: 'a2_loisirs',
    level: 'A2',
    emoji: '🎨',
    titleFr: 'Le temps libre, c’est sacré !',
    title: 'Hablar del tiempo libre',
    descFr: 'Tu parles de tes loisirs avec passion et en détail. Le temps libre, ça se raconte !',
    blurb: 'Hablaste de tus pasatiempos con detalle.',
    hint: 'Cuenta qué haces el fin de semana o en tu tiempo libre (je fais du sport, j’aime aller au cinéma…)',
    counters: { fois: 6 },
    counterLabels: { fois: 'frases de pasatiempos' },
    rule: 'Habla de un pasatiempo o de lo que hace en su tiempo libre con una frase distinta ("j\'aime aller au cinéma", "je fais du sport"…) → [[trophee:a2_loisirs:fois]]',
  },
  {
    id: 'a2_imparfait',
    // Determinista desde el 10/09/2026: Santiago (prueba A2 del 09/09) reportó
    // que el LLM "no lo reconoce en ningún contexto". Las terminaciones del
    // imparfait son regulares (-ais/-ais/-ait/-ions/-iez/-aient) → regex, ver
    // detectImparfaitSlots. Overlap asumido: el conditionnel présent y el
    // subjonctif de nous/vous tienen las mismas terminaciones y también cuentan.
    local: true,
    level: 'A2',
    emoji: '🧸',
    titleFr: 'Quand j’étais petit(e)...',
    title: 'El imparfait',
    descFr: 'Tu conjugues l’imparfait avec aisance pour évoquer des souvenirs et des habitudes passées.',
    blurb: 'Usaste el imparfait con todos los sujetos.',
    hint: 'Habla de recuerdos o costumbres pasadas en imparfait (quand j’étais petit, je jouais…)',
    counters: porSujeto(2),
    counterLabels: sujetoLabels('imparfait'),
    rule: `Conjuga un verbo en imparfait correctamente → según el sujeto usado: ${marcadoresSujetos('a2_imparfait')}`,
  },
  {
    id: 'a2_commerces',
    level: 'A2',
    emoji: '🛍️',
    titleFr: 'C’est dans le sac !',
    title: 'Hablar de compras',
    descFr: 'Tu te débrouilles dans les commerces : demander, comparer, acheter. Rien ne t’échappe !',
    blurb: 'Hablaste de tus hábitos de consumo en francés.',
    hint: 'Habla de tiendas, restaurantes o compras (où tu achètes, ce que tu achètes…)',
    counters: { fois: 10 },
    counterLabels: { fois: 'frases de consumo' },
    rule: 'Habla de sus hábitos de consumo (tiendas, restaurantes, compras) con una frase → [[trophee:a2_commerces:fois]] (máximo 3 por turno)',
  },
  {
    id: 'a2_aventure',
    level: 'A2',
    emoji: '🥾',
    titleFr: 'L’aventure continue',
    title: 'La aventura continúa',
    descFr: 'Tu as obtenu tous les trophées du niveau A2. Le voyage francophone continue !',
    blurb: '¡Obtuviste todos los trofeos del nivel A2!',
    hint: 'Desbloquea los 7 trofeos del nivel A2',
    counters: {},
    meta: true,
  },
];

/* ══════════════════════════ B1 ══════════════════════════ */

export const B1_TROPHIES: LevelTrophy[] = [
  {
    id: 'b1_conseils',
    level: 'B1',
    emoji: '💡',
    titleFr: 'Si j’étais toi...',
    title: 'Aconsejar y sugerir',
    descFr: 'Tu sais conseiller et suggérer avec tact, en utilisant les bonnes formules pour guider les autres.',
    blurb: 'Le diste consejos y sugerencias a Galia.',
    hint: 'Recomiéndale cosas a Galia (je te recommande de…, tu devrais…, si tu…)',
    counters: { fois: 10 },
    counterLabels: { fois: 'consejos' },
    rule: 'Te da un consejo o sugerencia NUEVA (una película, un libro, un lugar… que no haya recomendado antes en la conversación), con cualquier estructura (je te recommande de, je te propose de, tu devrais, si tu…) → [[trophee:b1_conseils:fois]]',
  },
  {
    id: 'b1_voyage',
    level: 'B1',
    emoji: '🧳',
    titleFr: 'Carnet de voyage',
    title: 'Contar un viaje',
    descFr: 'Tu racontes un voyage avec tous ses détails : départs, découvertes et anecdotes.',
    blurb: 'Contaste un viaje completo en pasado, con detalles.',
    hint: 'Cuéntale a Galia un viaje: transporte, alojamiento, actividades — en pasado',
    counters: { recit: 1 },
    counterLabels: { recit: 'relato de viaje' },
    rule: 'A lo largo de la conversación ha contado un viaje EN PASADO con al menos 3 de estos elementos: transporte, alojamiento, estadía, actividades (el relato puede acumularse en varios mensajes; emite el marcador cuando el conjunto ya sea un relato sustancial) → [[trophee:b1_voyage:recit]]',
  },
  {
    id: 'b1_conditionnel',
    level: 'B1',
    emoji: '🤔',
    titleFr: 'Il serait temps...',
    title: 'El condicional',
    descFr: 'Tu maîtrises le conditionnel pour exprimer souhaits, hypothèses et politesse.',
    blurb: 'Usaste el conditionnel présent con todos los sujetos.',
    hint: 'Expresa deseos e hipótesis en condicional (je voudrais…, on pourrait…)',
    counters: porSujeto(2),
    counterLabels: sujetoLabels('conditionnel'),
    rule: `Conjuga un verbo en conditionnel présent correctamente → según el sujeto usado: ${marcadoresSujetos('b1_conditionnel')}`,
  },
  {
    id: 'b1_discours',
    level: 'B1',
    emoji: '💬',
    titleFr: 'Il a dit que...',
    title: 'Discurso indirecto',
    descFr: 'Tu rapportes les paroles des autres sans perdre le fil : discours direct et indirect n’ont plus de secret pour toi.',
    blurb: 'Reportaste lo que dijeron otros (discours rapporté).',
    hint: 'Cuéntale a Galia lo que alguien más dijo ("le prof m’a dit que…")',
    counters: { fois: 10 },
    counterLabels: { fois: 'frases reportadas' },
    rule: 'Usa el discours rapporté (en presente o pasado) citando o reformulando lo que alguien más dijo ("le prof m\'a dit que…", "elle demande si…") → [[trophee:b1_discours:fois]]',
  },
  {
    id: 'b1_subjonctif',
    level: 'B1',
    emoji: '☝️',
    titleFr: 'Il faut que je te dise...',
    title: 'Subjuntivo de obligación',
    descFr: 'Tu utilises le subjonctif pour exprimer l’obligation et la nécessité comme un(e) expert(e).',
    blurb: 'Usaste il faut que + subjonctif con distintos sujetos.',
    hint: 'Expresa obligación con il faut que + subjonctif (il faut que je fasse…)',
    counters: porSujeto(2),
    counterLabels: sujetoLabels('il faut que'),
    rule: `Usa « il faut que » + subjonctif correctamente → según el sujeto de la subordinada: ${marcadoresSujetos('b1_subjonctif')}`,
  },
  {
    id: 'b1_films',
    level: 'B1',
    emoji: '🎬',
    titleFr: 'Ça, c’est du cinéma !',
    title: 'Hablar de cine',
    descFr: 'Tu parles d’un film avec conviction : intrigue, personnages, avis. Un(e) vrai(e) critique !',
    blurb: 'Hablaste de películas como crítico de cine.',
    hint: 'Háblale a Galia de películas: género, actores, historia (3 películas distintas)',
    counters: { fois: 3 },
    counterLabels: { fois: 'películas' },
    rule: 'Habla de una película NUEVA (distinta a las ya comentadas en la conversación) cubriendo género, actores Y algo de la historia → [[trophee:b1_films:fois]]',
  },
  {
    id: 'b1_comparaison',
    level: 'B1',
    emoji: '📈',
    titleFr: 'Toujours plus haut !',
    title: 'Comparar',
    descFr: 'Tu compares avec précision grâce aux adverbes de comparaison : plus, moins, aussi... que.',
    blurb: 'Comparaste con plus, moins, autant y aussi.',
    hint: 'Compara cosas, personas o situaciones (plus… que, moins… que, aussi… que, autant… que)',
    counters: { plus: 2, moins: 2, autant: 2, aussi: 2 },
    counterLabels: { plus: 'plus… que', moins: 'moins… que', autant: 'autant… que', aussi: 'aussi… que' },
    rule: 'Compara dos situaciones, personas u objetos con un adverbio de comparación → según el adverbio LITERAL usado: plus: [[trophee:b1_comparaison:plus]] · moins: [[trophee:b1_comparaison:moins]] · autant: [[trophee:b1_comparaison:autant]] · aussi: [[trophee:b1_comparaison:aussi]]',
  },
  {
    id: 'b1_systeme_d',
    level: 'B1',
    emoji: '🔧',
    titleFr: 'Le système D',
    title: 'Saber arreglárselas',
    descFr: 'Tu as obtenu tous les trophées du niveau B1. Tu sais te débrouiller en toutes circonstances !',
    blurb: '¡Obtuviste todos los trofeos del nivel B1!',
    hint: 'Desbloquea los 7 trofeos del nivel B1',
    counters: {},
    meta: true,
  },
];

/* ══════════════════════════ B2 ══════════════════════════ */

export const B2_TROPHIES: LevelTrophy[] = [
  {
    id: 'b2_livre',
    level: 'B2',
    emoji: '📖',
    titleFr: 'Chapitre après chapitre',
    title: 'Hablar de libros',
    descFr: 'Tu parles d’un livre avec passion : intrigue, personnages, ton avis de lecteur(trice) averti(e).',
    blurb: 'Hablaste de libros como lector experto.',
    hint: 'Háblale a Galia de libros: personajes, historia, lugar y autor (3 libros)',
    counters: { fois: 3 },
    counterLabels: { fois: 'libros' },
    rule: 'Habla de un libro NUEVO (distinto a los ya comentados) cubriendo personajes, historia, lugar o autor → [[trophee:b2_livre:fois]]',
  },
  {
    id: 'b2_point_de_vue',
    level: 'B2',
    emoji: '🛡️',
    titleFr: 'Bec et ongles',
    title: 'Defender un punto de vista',
    descFr: 'Tu défends ton point de vue avec des arguments solides, bec et ongles !',
    blurb: 'Defendiste tu punto de vista con argumentos sólidos.',
    hint: 'Da tu opinión argumentada (más de 80 palabras) sobre temas polémicos',
    counters: { fois: 3 },
    counterLabels: { fois: 'temas defendidos' },
    rule: 'Expresa su punto de vista sobre un tema polémico NUEVO en más de 80 palabras (puede sumar varios mensajes seguidos sobre el mismo tema) → [[trophee:b2_point_de_vue:fois]]',
  },
  {
    id: 'b2_gerondif',
    local: true,
    level: 'B2',
    emoji: '🏃',
    titleFr: 'Tout en avançant',
    title: 'El gérondif',
    descFr: 'Tu maîtrises le gérondif pour exprimer deux actions en même temps, tout en progressant en français !',
    blurb: 'Usaste el gérondif en tus relatos.',
    hint: 'Expresa dos acciones a la vez con en + participio (en marchant, en travaillant…)',
    counters: { fois: 10 },
    counterLabels: { fois: 'gérondifs' },
    rule: 'Usa un gérondif correctamente ("en travaillant", "tout en marchant"…) con un verbo NUEVO (no repetido en la conversación) → [[trophee:b2_gerondif:fois]]',
  },
  {
    id: 'b2_se_faire',
    local: true,
    level: 'B2',
    emoji: '🔄',
    titleFr: 'Se faire comprendre',
    title: 'Se faire + infinitivo',
    descFr: 'Tu maîtrises la forme passive avec « se faire + infinitif ». Désormais, tu sais toujours te faire comprendre !',
    blurb: 'Usaste la forma pasiva se faire + infinitivo.',
    hint: 'Usa se faire + infinitivo (je me suis fait couper les cheveux…)',
    counters: { fois: 10 },
    counterLabels: { fois: 'usos de se faire' },
    rule: 'Usa la forma pasiva « se faire + infinitif » correctamente en un relato → [[trophee:b2_se_faire:fois]]',
  },
  {
    id: 'b2_environnement',
    level: 'B2',
    emoji: '🌱',
    titleFr: 'Éco-logique',
    title: 'Hablar de ecología',
    descFr: 'Tu parles de l’environnement avec des arguments clairs sur l’écologie et le développement durable.',
    blurb: 'Hablaste de medio ambiente con argumentos.',
    hint: 'Habla de temas de ecología con argumentos (más de 80 palabras por tema)',
    counters: { fois: 3 },
    counterLabels: { fois: 'temas de ecología' },
    rule: 'Habla de un tema NUEVO relacionado con la ecología en más de 80 palabras (acumulables en la conversación) → [[trophee:b2_environnement:fois]]',
  },
  {
    id: 'b2_societe',
    level: 'B2',
    emoji: '🏙️',
    titleFr: 'Le monde a changé',
    title: 'Cambios de la sociedad',
    descFr: 'Tu analyses et présentes l’évolution de la société avec justesse et recul.',
    blurb: 'Analizaste cambios de la sociedad.',
    hint: 'Habla de cómo ha cambiado la sociedad (más de 80 palabras por tema)',
    counters: { fois: 3 },
    counterLabels: { fois: 'temas de sociedad' },
    rule: 'Habla de un cambio en la sociedad (tema NUEVO) en más de 80 palabras (acumulables) → [[trophee:b2_societe:fois]]',
  },
  {
    id: 'b2_conditionnel_passe',
    local: true,
    level: 'B2',
    emoji: '🔀',
    titleFr: 'J’aurais pu...',
    title: 'Condicional pasado',
    descFr: 'Tu maîtrises le conditionnel passé pour exprimer des regrets et des hypothèses non réalisées.',
    blurb: 'Expresaste arrepentimientos e hipótesis en conditionnel passé.',
    hint: 'Expresa lo que habrías hecho (j’aurais aimé…, tu aurais pu…)',
    counters: { fois: 10 },
    counterLabels: { fois: 'conditionnel passé' },
    rule: 'Conjuga un verbo en conditionnel passé correctamente ("j\'aurais aimé", "on aurait pu"…) con un verbo NUEVO → [[trophee:b2_conditionnel_passe:fois]]',
  },
  {
    id: 'b2_vol_solo',
    level: 'B2',
    emoji: '✈️',
    titleFr: 'Vol en solo',
    title: 'Vuelo en solitario',
    descFr: 'Tu as obtenu tous les trophées du niveau B2. Te voilà autonome en français !',
    blurb: '¡Obtuviste todos los trofeos del nivel B2!',
    hint: 'Desbloquea los 7 trofeos del nivel B2',
    counters: {},
    meta: true,
  },
];

/* ══════════════════════════ C (C1 y C2) ══════════════════════════ */

export const C_TROPHIES: LevelTrophy[] = [
  {
    id: 'c_connecteurs',
    level: 'C1',
    emoji: '⚖️',
    titleFr: 'Avocat(e) du diable',
    title: 'Debatir con conectores',
    descFr: 'Tu défends une position avec des connecteurs logiques avancés (certes, cependant, quoique...). Un vrai avocat du diable !',
    blurb: 'Debatiste un tema polémico con conectores lógicos avanzados.',
    hint: 'Debate un tema polémico usando 4 conectores avanzados (certes, cependant, quoique, néanmoins, en revanche)',
    counters: { debat: 1 },
    counterLabels: { debat: 'debate con 4 conectores' },
    rule: 'En un mismo debate sobre un tema polémico ha usado correctamente al menos 4 conectores lógicos avanzados distintos (certes, cependant, quoique, néanmoins, en revanche…) — pueden acumularse en varios mensajes del mismo debate → [[trophee:c_connecteurs:debat]]',
  },
  {
    id: 'c_humour',
    level: 'C1',
    emoji: '😏',
    titleFr: 'Maître (Maîtresse) du second degré',
    title: 'Humor e ironía',
    descFr: 'Tu manies l’humour et l’ironie avec finesse. Impossible de te prendre au premier degré !',
    blurb: 'Hiciste humor e ironía en francés.',
    hint: 'Haz un chiste, juego de palabras o ironía en francés sobre temas de sociedad',
    counters: { fois: 2 },
    counterLabels: { fois: 'situaciones de humor' },
    rule: 'Hace un chiste, juego de palabras o ironía INTENCIONAL en francés sobre un tema de sociedad (estereotipos, política, vida cotidiana) → [[trophee:c_humour:fois]]',
  },
  {
    id: 'c_registres',
    level: 'C1',
    emoji: '🦎',
    titleFr: 'Caméléon linguistique',
    title: 'Cambiar de registro',
    descFr: 'Tu passes du registre soutenu au familier sans effort. Un vrai caméléon linguistique !',
    blurb: 'Cambiaste de registro según el interlocutor.',
    hint: 'Usa los tres registros según el contexto: sostenido, estándar y familiar/argot',
    counters: { soutenu: 1, standard: 1, familier: 1 },
    counterLabels: { soutenu: 'registro sostenido', standard: 'registro estándar', familier: 'familiar/argot' },
    rule: 'Usa un registro de lengua adecuado al interlocutor o contexto que esté simulando → según el registro: sostenido (jefe, administración): [[trophee:c_registres:soutenu]] · estándar (profesor): [[trophee:c_registres:standard]] · familiar/argot (amigo): [[trophee:c_registres:familier]]',
  },
  {
    id: 'c_critique',
    level: 'C1',
    emoji: '🖋️',
    titleFr: 'Critique littéraire',
    title: 'Crítica literaria',
    descFr: 'Tu analyses une œuvre francophone avec un vrai regard critique. Bienvenue au club des lecteurs exigeants !',
    blurb: 'Analizaste una obra francófona con mirada crítica.',
    hint: 'Comenta una obra o autor francófono comprometido (Camus, Hugo, Slimani…) con argumentos propios',
    counters: { analyse: 1 },
    counterLabels: { analyse: 'análisis literario' },
    rule: 'Comenta una obra, autor o movimiento literario francófono comprometido (Camus, Sartre, Voltaire, Hugo, Leïla Slimani, Aimé Césaire…) con argumentos PROPIOS y un análisis completo (acumulable en la conversación) → [[trophee:c_critique:analyse]]',
  },
  {
    id: 'c_reporter',
    level: 'C1',
    emoji: '📰',
    titleFr: 'Grand reporter',
    title: 'Comentar la actualidad',
    descFr: 'Tu résumes et commentes l’actualité en distinguant les faits des opinions. Une vraie plume de journaliste !',
    blurb: 'Comentaste noticias distinguiendo hechos de opiniones.',
    hint: 'Resume una noticia o polémica distinguiendo hechos de opiniones, con tu matiz',
    counters: { fois: 2 },
    counterLabels: { fois: 'noticias comentadas' },
    rule: 'Resume una noticia o polémica social distinguiendo hechos de opiniones y aportando su propio matiz → [[trophee:c_reporter:fois]]',
  },
  {
    id: 'c_grammairien',
    level: 'C1',
    emoji: '📚',
    titleFr: 'Grammairien(ne) hors pair',
    title: 'Gramática avanzada',
    descFr: 'Tu maîtrises les structures grammaticales les plus subtiles du français. Un(e) vrai(e) grammairien(ne) hors pair !',
    blurb: 'Usaste las estructuras gramaticales más sutiles.',
    hint: 'Usa subjonctif passé, concordance des temps, discours indirecto en pasado y voz pasiva compleja',
    counters: { subj_passe: 1, concordance: 1, discours_passe: 1, passive: 1 },
    counterLabels: {
      subj_passe: 'subjonctif passé',
      concordance: 'concordance des temps',
      discours_passe: 'discours indirect au passé',
      passive: 'voix passive compleja',
    },
    rule: 'Al narrar un hecho pasado usa correctamente una estructura avanzada → según la estructura: subjonctif passé: [[trophee:c_grammairien:subj_passe]] · concordance des temps: [[trophee:c_grammairien:concordance]] · discours indirect au passé: [[trophee:c_grammairien:discours_passe]] · voix passive compleja: [[trophee:c_grammairien:passive]]',
  },
  {
    id: 'c_idiomatiques',
    level: 'C1',
    emoji: '🥐',
    titleFr: 'Ça ne mange pas de pain',
    title: 'Expresiones idiomáticas',
    descFr: 'Tu utilises les expressions idiomatiques comme un(e) vrai(e) natif(ve). Ça ne mange pas de pain, mais ça impressionne !',
    blurb: 'Usaste expresiones idiomáticas como nativo.',
    hint: 'Usa expresiones idiomáticas o refranes franceses en contexto (5 distintas)',
    counters: { fois: 5 },
    counterLabels: { fois: 'expresiones' },
    rule: 'Usa correctamente y en contexto una expresión idiomática o refrán francés NUEVO → [[trophee:c_idiomatiques:fois]]',
  },
  {
    id: 'c_orateur',
    level: 'C1',
    emoji: '🎙️',
    titleFr: 'Grand orateur, grande oratrice',
    title: 'Discurso persuasivo',
    descFr: 'Tu structures un discours qui convainc, du premier au dernier mot. Quel grand orateur, quelle grande oratrice !',
    blurb: 'Estructuraste un discurso persuasivo completo.',
    hint: 'Presenta un discurso persuasivo breve: introducción, argumentos y conclusión',
    counters: { discours: 1 },
    counterLabels: { discours: 'discurso completo' },
    rule: 'Estructura y presenta un discurso persuasivo breve sobre un tema polémico CON introducción, argumentos y conclusión (acumulable en varios mensajes) → [[trophee:c_orateur:discours]]',
  },
  {
    id: 'c_citoyen',
    level: 'C1',
    emoji: '🌍',
    titleFr: 'Citoyen(ne) du monde',
    title: 'Grandes debates mundiales',
    descFr: 'Tu débats des grands enjeux mondiaux avec nuance, sans jamais tomber dans le noir ou blanc. Un(e) vrai(e) citoyen(ne) du monde !',
    blurb: 'Debatiste un tema global mostrando pros y contras.',
    hint: 'Opina con matices sobre un tema global (clima, migraciones, IA…) mostrando al menos 2 puntos de vista',
    counters: { debat: 1 },
    counterLabels: { debat: 'debate con matices' },
    rule: 'Opina sobre un tema global polémico (cambio climático, desigualdades, migraciones, ética de la IA, energía nuclear…) presentando al menos DOS puntos de vista → [[trophee:c_citoyen:debat]]',
  },
  {
    id: 'c_globetrotter',
    level: 'C1',
    emoji: '🗺️',
    titleFr: 'Globe-trotter francophone',
    title: 'La francofonía',
    descFr: 'Tu explores la Francophonie au-delà de la France : Québec, Afrique, Belgique, Suisse... Un(e) vrai(e) globe-trotter francophone !',
    blurb: 'Exploraste la diversidad del mundo francófono.',
    hint: 'Habla de particularidades de al menos 2 regiones francófonas (Québec, Bélgica, África…)',
    counters: { fois: 2 },
    counterLabels: { fois: 'regiones francófonas' },
    rule: 'Habla de particularidades culturales o debates propios de una región francófona NUEVA fuera de Francia (Québec, Bélgica, Suiza, África francófona…) → [[trophee:c_globetrotter:fois]]',
  },
  {
    id: 'c_sommet',
    level: 'C1',
    emoji: '🏔️',
    titleFr: 'Au sommet de la Francophonie',
    title: 'La cima de la francofonía',
    descFr: 'Tu as obtenu tous les trophées du niveau C. Te voilà au sommet de la Francophonie !',
    blurb: '¡Obtuviste todos los trofeos del nivel C!',
    hint: 'Desbloquea los 10 trofeos del nivel C',
    counters: {},
    meta: true,
  },
];

/* ══════════════════ Académicos (datos AEC, evaluados en galia-ws) ══════════════════ */

export const AEC_TROPHIES: LevelTrophy[] = [
  {
    id: 'aec_co', level: 'AEC', emoji: '👂',
    titleFr: 'L’oreille fine !', title: 'Comprensión oral',
    descFr: 'Tu as obtenu plus de 8 en compréhension orale. Rien ne t’échappe, tu as l’oreille fine !',
    blurb: 'Sacaste más de 8 en comprensión oral.',
    hint: 'Obtén una nota de comprensión oral (CO) de 8 o más en tu curso de la AF',
    counters: {}, dynamic: 1,
  },
  {
    id: 'aec_ce', level: 'AEC', emoji: '🔍',
    titleFr: 'Œil de lynx', title: 'Comprensión escrita',
    descFr: 'Tu as obtenu plus de 8 en compréhension écrite. Aucun texte ne te résiste, tu as l’œil de lynx !',
    blurb: 'Sacaste más de 8 en comprensión escrita.',
    hint: 'Obtén una nota de comprensión escrita (CE) de 8 o más en tu curso de la AF',
    counters: {}, dynamic: 1,
  },
  {
    id: 'aec_po', level: 'AEC', emoji: '🗣️',
    titleFr: 'Beau parleur, belle parleuse', title: 'Producción oral',
    descFr: 'Tu as obtenu plus de 8 en production orale. Tu manies les mots à voix haute comme personne !',
    blurb: 'Sacaste más de 8 en producción oral.',
    hint: 'Obtén una nota de producción oral (PO) de 8 o más en tu curso de la AF',
    counters: {}, dynamic: 1,
  },
  {
    id: 'aec_pe', level: 'AEC', emoji: '✒️',
    titleFr: 'Plume d’or', title: 'Producción escrita',
    descFr: 'Tu as obtenu plus de 8 en production écrite. Ta plume est en or, continue d’écrire !',
    blurb: 'Sacaste más de 8 en producción escrita.',
    hint: 'Obtén una nota de producción escrita (PE) de 8 o más en tu curso de la AF',
    counters: {}, dynamic: 1,
  },
  {
    id: 'aec_sans_faute', level: 'AEC', emoji: '⭐',
    titleFr: 'Sans faute !', title: 'Sin fallas',
    descFr: 'Tu as obtenu plus de 8 dans toutes les compétences du module. Un sans-faute total !',
    blurb: 'Todas tus notas del módulo por encima de 8.',
    hint: 'Obtén 8 o más en TODAS las competencias de un módulo (CO, CE, PO, PE)',
    counters: {}, dynamic: 1,
  },
  {
    id: 'aec_assiduite', level: 'AEC', emoji: '📅',
    titleFr: 'Présent(e) !', title: 'Asistencia perfecta',
    descFr: 'Tu as assisté à 100% des cours. Toujours présent(e), jamais absent(e) !',
    blurb: 'Asististe al 100% de las clases.',
    hint: 'Asiste al 100% de las clases de tu módulo',
    counters: {}, dynamic: 1,
  },
  {
    // Mesa del 29/09/2026: « hay un logro que es tener la mejor nota del
    // curso » (Santiago); Greg → « el que tenga el mayor Total de la lista ».
    // No está en la hoja Académicos del Excel: nombre y textos propuestos por
    // LidarIT, a confirmar con Santiago. Lo evalúa galia-ws (aec_meilleur).
    id: 'aec_meilleur', level: 'AEC', emoji: '🥇',
    titleFr: 'Premier(ère) de la classe', title: 'Mejor nota del curso',
    descFr: 'Tu as obtenu la meilleure note de ton cours. Premier(ère) de la classe, chapeau !',
    blurb: 'Tuviste la mejor nota total de tu curso.',
    hint: 'Obtén el mejor Total de tu curso en la AF',
    counters: {}, dynamic: 1,
  },
  {
    id: 'aec_sommet', level: 'AEC', emoji: '⛰️',
    titleFr: 'Le sommet !', title: 'La cima',
    descFr: 'Tu as obtenu 4,5 ou plus dans un module. Tu atteins le sommet !',
    blurb: 'Nota final de módulo de 4.5 o superior.',
    hint: 'Obtén una nota final de módulo de 4.5 o superior',
    counters: {}, dynamic: 1,
  },
  {
    id: 'aec_trio', level: 'AEC', emoji: '🏅',
    titleFr: 'Le trio gagnant', title: 'El trío ganador',
    descFr: 'Tu as obtenu 4,5 ou plus dans plusieurs modules. Trois fois excellent(e), quel trio gagnant !',
    blurb: 'Nota final de 4.5+ en tres módulos.',
    hint: 'Obtén 4.5 o más en la nota final de 3 módulos',
    counters: {}, dynamic: 3,
  },
];

/* ══════════════ Kiosco de recepción (demo, pedido de Santiago 26/08) ══════════════
 * En la pantalla de recepción el visitante pasa 1-2 minutos: el catálogo A1
 * completo es inalcanzable y satura la vitrina. Santiago pidió SOLO tres
 * logros, uno por gesto conversacional: saludar, preguntar y despedirse.
 * Se desbloquean al primer acierto (umbral 1) para que el toast salga rápido.
 */
export const KIOSK_TROPHIES: LevelTrophy[] = [
  {
    id: 'kiosk_saluer',
    level: 'A1',
    emoji: '👋',
    titleFr: 'Bonjour !',
    title: 'Saludar a Galia',
    descFr: 'Tu as salué Galia en français. La conversation commence !',
    blurb: 'Saludaste a Galia en francés.',
    hint: 'Salúdala en francés: « Bonjour ! »',
    counters: { fois: 1 },
    counterLabels: { fois: 'saludo' },
    rule: 'Saluda en francés (bonjour, salut, bonsoir, coucou…) → [[trophee:kiosk_saluer:fois]]',
  },
  {
    id: 'kiosk_question',
    level: 'A1',
    emoji: '❓',
    titleFr: 'Et toi ?',
    title: 'Preguntarle algo',
    descFr: 'Tu as posé une question à Galia. La curiosité, c’est le début du français !',
    blurb: 'Le hiciste una pregunta a Galia.',
    hint: 'Pregúntale algo en francés: « Comment ça va ? »',
    counters: { fois: 1 },
    counterLabels: { fois: 'pregunta' },
    rule: "Te hace una pregunta en francés (comment, pourquoi, où, est-ce que, qu'est-ce que…) → [[trophee:kiosk_question:fois]]",
  },
  {
    id: 'kiosk_adieu',
    level: 'A1',
    emoji: '🌙',
    titleFr: 'Au revoir !',
    title: 'Despedirse',
    descFr: 'Tu as dit au revoir comme un vrai francophone. À bientôt !',
    blurb: 'Te despediste en francés.',
    hint: 'Despídete en francés: « Au revoir ! »',
    counters: { fois: 1 },
    counterLabels: { fois: 'despedida' },
    rule: 'Se despide en francés (au revoir, à bientôt, à plus, bonne journée, salut de despedida…) → [[trophee:kiosk_adieu:fois]]',
  },
];

/** Catálogo por nivel. C1 y C2 comparten la hoja C del Excel. */
export const LEVEL_TROPHIES: Partial<Record<MCERLevel, LevelTrophy[]>> = {
  A1: A1_TROPHIES,
  A2: A2_TROPHIES,
  B1: B1_TROPHIES,
  B2: B2_TROPHIES,
  C1: C_TROPHIES,
  C2: C_TROPHIES,
};

export const TROPHIES_BY_ID: Record<string, LevelTrophy> = Object.fromEntries(
  [...Object.values(LEVEL_TROPHIES).flat(), ...AEC_TROPHIES, ...KIOSK_TROPHIES].map((t) => [t.id, t])
);

/**
 * Trofeos vigentes para el estudiante. En el kiosco de recepción devuelve el
 * catálogo reducido de 3 (ver KIOSK_TROPHIES) sin importar el nivel: de aquí
 * beben el cofre, el paisaje, el prompt y achievementService, así que el
 * kiosco queda consistente en una sola decisión.
 */
export function trophiesForLevel(level?: MCERLevel | null): LevelTrophy[] {
  if (isKioskMode()) return KIOSK_TROPHIES;
  if (!level) return [];
  return LEVEL_TROPHIES[level] || [];
}

/** Suma de metas de un trofeo (para mostrar progreso "3/6"). */
export function trophyTarget(t: LevelTrophy): number {
  if (t.dynamic) return t.dynamic;
  return Object.values(t.counters).reduce((a, b) => a + b, 0);
}

/** Progreso actual de un trofeo dado su mapa de contadores por casilla. */
export function trophyProgress(
  t: LevelTrophy,
  counts?: Record<string, number>
): number {
  if (!counts) return 0;
  if (t.dynamic) {
    return Math.min(Object.values(counts).filter((n) => n >= 1).length, t.dynamic);
  }
  return Object.entries(t.counters).reduce(
    (acc, [slot, target]) => acc + Math.min(counts[slot] || 0, target),
    0
  );
}

/** ¿Todas las casillas del trofeo llegaron a su meta? */
export function isTrophyComplete(
  t: LevelTrophy,
  counts?: Record<string, number>
): boolean {
  if (t.meta) return false; // los de plata se resuelven aparte
  if (t.dynamic) {
    return Object.values(counts || {}).filter((n) => n >= 1).length >= t.dynamic;
  }
  return Object.entries(t.counters).every(
    ([slot, target]) => (counts?.[slot] || 0) >= target
  );
}

/**
 * Refina los reconocimientos del LLM contra el texto literal del estudiante.
 * Caso a1_localiser: el modelo confunde a veces la casilla (emite pays_en para
 * "au Brésil") — aquí el modelo solo decide QUE hubo un uso correcto de
 * preposición de lugar, y la casilla la decide la preposición literal escrita
 * (à/en/au/aux + lugar con mayúscula). Si la regex no encuentra ninguna
 * (p. ej. lugar en minúscula), se conservan las casillas del modelo.
 */
export function refineRecognitions(
  items: Array<{ id: string; slot: string }>,
  lastUserMessage: string
): Array<{ id: string; slot: string }> {
  if (!items.some((i) => i.id === 'a1_localiser') || !lastUserMessage) return items;
  const text = ` ${lastUserMessage} `;
  const slots: string[] = [];
  // OJO: \b es ASCII en JS y no ve frontera antes de "à" — se usa \s (el texto
  // viene con espacios de relleno en los bordes).
  if (/\s[àa]\s+[A-ZÀ-Ý]/u.test(text)) slots.push('ville_a');
  if (/\ben\s+[A-ZÀ-Ý]/u.test(text)) slots.push('pays_en');
  if (/\bau\s+[A-ZÀ-Ý]/u.test(text)) slots.push('pays_au');
  if (/\baux\s+[A-ZÀ-Ý]/u.test(text)) slots.push('pays_aux');
  if (slots.length === 0) return items;
  return items
    .filter((i) => i.id !== 'a1_localiser')
    .concat(slots.map((slot) => ({ id: 'a1_localiser', slot })));
}

/* ══════════════ Reconocimiento local (trofeos `local: true`) ══════════════ */

/**
 * Normaliza para las regexes: minúsculas, apóstrofos tipográficos → rectos,
 * espacios colapsados y bordes con relleno (los \b de JS son ASCII y no ven
 * frontera junto a letras acentuadas — mismo gotcha que refineRecognitions).
 */
function normalizeForDetection(text: string): string {
  return ` ${text.toLowerCase().replace(/[’‘]/g, "'").replace(/\s+/g, ' ')} `;
}

/**
 * Imparfait por sujeto sobre un texto ya normalizado (minúsculas, apóstrofos
 * rectos, espacios simples). Devuelve, por casilla, el conjunto de formas
 * verbales distintas encontradas.
 *
 * Terminaciones: je/tu -ais, il/elle/on -ait, nous -ions, vous -iez,
 * ils/elles -aient. Se exige raíz de 2+ letras y se descartan las formas del
 * PRESENTE que terminan igual: "je sais/vais/fais/connais", "il fait/sait",
 * los verbos en -ier ("nous étudions", "vous criez") y "ils paient/essaient".
 * El conditionnel présent ("je parlerais") y el subjonctif de nous/vous
 * ("que nous parlions") comparten terminación y cuentan: en A2 es un exceso
 * aceptable frente a no reconocer nada.
 */
const IMPARFAIT_PRESENT_AIS = new Set([
  'sais', 'vais', 'fais', 'connais', 'reconnais', 'parais', 'apparais', 'disparais',
  'nais', 'renais', 'plais', 'tais', 'hais', 'extrais', 'distrais', 'soustrais', 'trais',
]);
const IMPARFAIT_PRESENT_AIT = new Set([
  'fait', 'refait', 'défait', 'defait', 'satisfait', 'sait', 'plait', 'plaît', 'tait',
  'hait', 'extrait', 'distrait', 'soustrait', 'trait', 'abstrait', 'connait', 'connaît',
  'reconnait', 'reconnaît', 'parait', 'paraît', 'apparait', 'apparaît', 'disparait',
  'disparaît', 'nait', 'naît', 'renait', 'renaît',
]);
// Raíces de verbos en -ier frecuentes: su presente "nous Xions / vous Xiez"
// no es imparfait (el imparfait lleva doble i: "nous étudiions").
const IMPARFAIT_IER_ROOTS = new Set([
  'étud', 'etud', 'oubl', 'cr', 'pr', 'cop', 'remerc', 'appréc', 'apprec', 'vérif', 'verif',
  'modif', 'pl', 'sk', 'env', 'mar', 'var', 'conf', 'justif', 'identif', 'signif',
  'photograph', 'assoc', 'négoc', 'negoc', 'expéd', 'exped', 'licenc', 'bénéfic', 'benefic',
  'planif', 'simplif', 'multipl', 'qualif', 'certif', 'spécif', 'specif', 'publ', 'suppl',
  'mendi', 'inqui', 'suppli', 'défi', 'defi', 'méfi', 'mefi', 'fi', 'ri', 'li', 'ni', 'sci',
]);
const IMPARFAIT_PRESENT_AIENT = new Set([
  'paient', 'essaient', 'balaient', 'effraient', 'égaient', 'egaient', 'bégaient', 'begaient',
  'délaient', 'delaient', 'enraient', 'aient', 'soient',
]);

/**
 * Entre el pronombre sujeto y el verbo caben la negación y los clíticos:
 * "je ne pouvais pas", "il y avait", "on me disait", "je m'appelais",
 * "j'en avais", "nous ne pouvons pas". Fix del 10/09 (2.ª ronda A2 de
 * Santiago): sus formas negadas no contaban en pouvoir/vouloir ni en imparfait.
 */
const PRE_VERB = "(?:ne\\s+|n')?(?:(?:me|te|se|le|la|les|lui|leur|nous|vous|y|en)\\s+|[mtsl]')*";

/** Pronombres sujeto por casilla (sin la 3.ª del singular, que tiene extras). */
const SUBJ_JE = "(?:je\\s+|j')";
const SUBJ_TU = 'tu\\s+';
// "c'était", "ça ne m'intéressait pas" también son 3.ª del singular.
const SUBJ_IL = "(?:(?:il|elle|on|ça|ca|cela)\\s+|c')";
const SUBJ_NOUS = 'nous\\s+';
const SUBJ_VOUS = 'vous\\s+';
const SUBJ_ILS = '(?:ils|elles)\\s+';

function detectImparfaitSlots(text: string): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  const put = (slot: string, verb: string) => {
    if (!out.has(slot)) out.set(slot, new Set());
    out.get(slot)!.add(verb);
  };
  const scan = (
    slot: string,
    subject: string,
    ending: string,
    reject: (verb: string, stem: string) => boolean
  ) => {
    const re = new RegExp(`\\b${subject}${PRE_VERB}(\\p{L}{2,}${ending})\\b`, 'gu');
    for (const m of text.matchAll(re)) {
      const verb = m[1];
      const stem = verb.slice(0, verb.length - ending.length);
      if (!reject(verb, stem)) put(slot, verb);
    }
  };
  const isIer = (stem: string) => IMPARFAIT_IER_ROOTS.has(stem);
  scan('je', SUBJ_JE, 'ais', (v) => IMPARFAIT_PRESENT_AIS.has(v));
  scan('tu', SUBJ_TU, 'ais', (v) => IMPARFAIT_PRESENT_AIS.has(v));
  scan('il_elle', SUBJ_IL, 'ait', (v) => IMPARFAIT_PRESENT_AIT.has(v));
  scan('nous', SUBJ_NOUS, 'ions', (_v, s) => isIer(s));
  scan('vous', SUBJ_VOUS, 'iez', (_v, s) => isIer(s));
  scan('ils_elles', SUBJ_ILS, 'aient', (v) => IMPARFAIT_PRESENT_AIENT.has(v));

  // Sujetos nominales ("ma créativité n'était pas top", "mon père travaillait",
  // "mes parents habitaient à Lyon"): sin pronombre delante no hay forma de
  // saber la persona salvo en dos casos inequívocos:
  //  - "était/avait" solo existen en 3.ª del singular;
  //  - la terminación -aient solo existe en 3.ª del plural (salvo los presentes
  //    de la lista de exclusión).
  for (const m of text.matchAll(/\b(?:n')?(était|etait|avait)\b/gu)) put('il_elle', m[1]);
  for (const m of text.matchAll(/\b(\p{L}{2,}aient)\b/gu)) {
    if (!IMPARFAIT_PRESENT_AIENT.has(m[1])) put('ils_elles', m[1]);
  }
  return out;
}

/**
 * pouvoir/vouloir en presente por casilla. Admite negación y clíticos entre
 * sujeto y verbo ("elle ne peut pas", "tu veux m'écouter" → "tu veux") y la
 * inversión interrogativa ("peux-tu", "voulez-vous"). "on" cuenta como il/elle.
 */
const POUVOIR_VOULOIR_FORMS: Array<[slot: string, subject: string, pronouns: string, form: string]> = [
  ['pouvoir_je', SUBJ_JE, 'je', 'peux'],
  ['pouvoir_tu', SUBJ_TU, 'tu', 'peux'],
  ['pouvoir_il', SUBJ_IL, 'il|elle|on', 'peut'],
  ['pouvoir_nous', SUBJ_NOUS, 'nous', 'pouvons'],
  ['pouvoir_vous', SUBJ_VOUS, 'vous', 'pouvez'],
  ['pouvoir_ils', SUBJ_ILS, 'ils|elles', 'peuvent'],
  ['vouloir_je', SUBJ_JE, 'je', 'veux'],
  ['vouloir_tu', SUBJ_TU, 'tu', 'veux'],
  ['vouloir_il', SUBJ_IL, 'il|elle|on', 'veut'],
  ['vouloir_nous', SUBJ_NOUS, 'nous', 'voulons'],
  ['vouloir_vous', SUBJ_VOUS, 'vous', 'voulez'],
  ['vouloir_ils', SUBJ_ILS, 'ils|elles', 'veulent'],
];

function detectPouvoirVouloirSlots(text: string): string[] {
  const slots: string[] = [];
  for (const [slot, subject, pronouns, form] of POUVOIR_VOULOIR_FORMS) {
    const direct = new RegExp(`\\b${subject}${PRE_VERB}${form}\\b`, 'u');
    const inverted = new RegExp(`\\b${form}-(?:${pronouns})\\b`, 'u');
    if (direct.test(text) || inverted.test(text)) slots.push(slot);
  }
  return slots;
}

/* ── A2: partitivos por regex (desde el 30/09/2026) ──
 * Mesa del 29/09/2026: Santiago usó « du café », « des œufs », « de la farine »,
 * « de l'eau », « du sel »… y el LLM solo marcó du y de la (de l' y des quedaron
 * en 0 con la casilla a medias). Igual que imparfait y pouvoir/vouloir: la
 * forma literal pasa a regex. Cuenta solo con CONTEXTO partitivo (lista de
 * anclas abajo) o en elipsis tras puntuación; así quedan fuera « de + le/les »
 * (« le secteur de la santé », « je viens du Canada », « on parle des
 * vacances »), la negación y las cantidades (« pas du », « beaucoup des »),
 * las locuciones (« du coup », « de la part », « des fois ») y los nombres
 * propios (mayúscula en el texto ORIGINAL, la normalización la borra).
 * Devuelve, por casilla, el conjunto de sustantivos distintos (« du café » y
 * « du sel » en un mismo mensaje son dos usos; la casilla admite 2).
 */
// Contexto que hace partitivo al artículo: verbos de consumo, preparación,
// compra, posesión y existencia (« je mange du pain », « il y a de la
// confiture », « j'ai du courage »), « avec », rellenos orales (« euh de la
// farine ») y la elipsis tras puntuación (« c'est vrai, du fromage, de
// l'huile »). Sin este contexto, « de la/du/des » es casi siempre de + artículo
// (« le secteur de la santé », « ça dépend des sujets ») — sondeo del
// 30/09/2026 sobre 781 mensajes reales.
const PARTITIF_ER_STEMS = [
  'mang', 'achet', 'achèt', 'cuisin', 'prépar', 'prepar', 'command', 'ajout', 'goût', 'gout',
  'cherch', 'trouv', 'donn', 'utilis', 'apport', 'vers', 'mélang', 'melang', 'cultiv', 'coup',
  'réchauff', 'rechauff', 'consomm', 'grignot', 'dégust', 'degust', 'avai', 'gard', 'rest',
  'manqu', 'demand', 'propos', 'offr', 'achèter',
];
const PARTITIF_ER_ENDINGS = [
  'e', 'es', 'ons', 'ez', 'ent', 'é', 'er', 'ais', 'ait', 'ions', 'iez', 'aient',
  'erai', 'eras', 'era', 'erons', 'erez', 'eront', 'erais', 'erait', 'eraient', 'eons', 'eais', 'eait', 'eaient',
];
const PARTITIF_ANCHORS = new Set<string>([
  // avoir / il y a / falloir
  'ai', 'as', 'a', 'avons', 'avez', 'ont', 'avais', 'avait', 'avions', 'aviez', 'avaient',
  'aurai', 'auras', 'aura', 'aurons', 'aurez', 'auront', 'aurais', 'aurait', 'eu',
  'faut', 'fallait', 'faudrait', 'faudra',
  // boire, prendre, vouloir, faire, mettre, servir, vendre, choisir, produire
  'bois', 'boit', 'buvons', 'buvez', 'boivent', 'bu', 'boire', 'buvais', 'buvait', 'buvions', 'buviez', 'buvaient',
  'boirai', 'boiras', 'boira', 'boirons', 'boirez', 'boiront',
  'prends', 'prend', 'prenons', 'prenez', 'prennent', 'pris', 'prendre', 'prenais', 'prenait', 'prenions',
  'preniez', 'prenaient', 'prendrai', 'prendras', 'prendra', 'prendrons', 'prendrez', 'prendront',
  'veux', 'veut', 'voulons', 'voulez', 'veulent', 'voulu', 'vouloir', 'voulais', 'voulait', 'voulions',
  'vouliez', 'voulaient', 'voudrais', 'voudrait', 'voudrions', 'voudriez', 'voudraient',
  'fais', 'fait', 'faisons', 'faites', 'font', 'faire', 'faisais', 'faisait', 'faisions', 'faisiez',
  'faisaient', 'ferai', 'feras', 'fera', 'ferons', 'ferez', 'feront', 'ferais', 'ferait',
  'mets', 'met', 'mettons', 'mettez', 'mettent', 'mis', 'mettre', 'mettais', 'mettait', 'mettaient',
  'sers', 'sert', 'servons', 'servez', 'servent', 'servi', 'servir', 'servais', 'servait',
  'vends', 'vend', 'vendons', 'vendez', 'vendent', 'vendu', 'vendre',
  'choisis', 'choisit', 'choisissons', 'choisissez', 'choisissent', 'choisi', 'choisir',
  'produis', 'produit', 'produisons', 'produisez', 'produisent', 'produire',
  // conectores y rellenos que dejan el artículo en posición partitiva
  'avec', 'encore', 'aussi', 'surtout', 'seulement', 'juste', 'plutôt', 'plutot', 'souvent',
  'toujours', 'parfois', 'quelquefois', 'que', 'et', 'ou', 'puis', 'euh', 'hum', 'ben', 'bah',
  'bon', 'alors', 'donc', 'voilà', 'voila', 'oui', 'non', 'quoi', 'est', 'était', 'etait',
  ...PARTITIF_ER_STEMS.flatMap((st) => PARTITIF_ER_ENDINGS.map((e) => st + e)),
]);
const PARTITIF_CHAIN = new Set(['et', 'ou', 'puis']);
const PARTITIF_NOT_NOUN = new Set([
  'coup', 'moins', 'tout', 'reste', 'côté', 'cote', 'fait', 'début', 'debut', 'milieu',
  'matin', 'soir', 'jour', 'mois', 'fois', 'part', 'plupart', 'même', 'meme', 'moment',
  'monde', 'gens', 'cours', 'français', 'francais', 'espagnol', 'anglais', 'et', 'ou', 'de',
  'ma', 'mon', 'mes', 'ta', 'ton', 'tes', 'sa', 'son', 'ses', 'nos', 'vos', 'leur', 'leurs',
  'ce', 'cet', 'cette', 'ces', 'la', 'le', 'les', 'un', 'une', 'autres', 'autre',
]);
const PARTITIF_SLOT: Record<string, string> = { du: 'du', des: 'des', 'de la': 'de_la', "de l'": 'de_l' };

function detectPartitifSlots(text: string, original: string): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  // Palabras con mayúscula inicial en el original (nombres propios).
  const caps = new Set((original.match(/[A-ZÀ-Ý]\p{L}+/gu) || []).map((w) => w.toLowerCase()));
  // Dos contextos posibles, y uno de los dos es OBLIGATORIO (si la palabra
  // previa fuera opcional, el motor la saltaría y « pas du café » parecería
  // elíptico): (a) elipsis = inicio o puntuación antes del artículo; (b) una
  // palabra previa, con su elisión si la lleva (« l'idée de la question » →
  // prev = « idée »).
  const re = /(?:(^|[^\p{L}\s'])\s*|(?:^|[^\p{L}])(?:\p{L}{1,2}')?(\p{L}+)\s+)(?:(du|des|de la)\s+|(de l')\s*)(\p{L}{2,})(?!\p{L})/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const prev = m[2] || ''; // '' = elipsis (m[1])
    const form = m[3] || m[4];
    const noun = m[5];
    const ok =
      (prev === '' || PARTITIF_ANCHORS.has(prev)) &&
      // « et / ou / puis » solo encadenan: valen si ya hubo un partitivo antes en
      // el mensaje (« du courage et de la chance »), no « la maison et des courses ».
      !(PARTITIF_CHAIN.has(prev) && out.size === 0) &&
      !PARTITIF_NOT_NOUN.has(noun) &&
      !caps.has(noun);
    if (!ok) {
      // Un rechazo no debe "consumir" el sustantivo: « de l'organisation du
      // planning » rechazaba « de l'organisation » y luego veía « du planning »
      // como elíptico. Se retoma justo después del inicio del rechazado.
      re.lastIndex = m.index + 1;
      continue;
    }
    const slot = PARTITIF_SLOT[form];
    if (!out.has(slot)) out.set(slot, new Set());
    out.get(slot)!.add(noun);
  }
  return out;
}

/* ── B2: formas gramaticales por regex (desde el 11/09/2026) ──
 * Prueba E2E del 11/09: tras 9 usos correctos de «se faire + infinitif» el LLM
 * no emitió ni un marcador; gérondif y conditionnel passé, ~1 de cada 4 usos, y
 * además emitió marcadores sin fundamento (un gérondif en un mensaje sin
 * gérondif). Igual que en A1/A2: lo que es forma literal pasa a regex y el LLM
 * se queda con lo que exige juicio (libros, opiniones, ecología, sociedad).
 * "Verbo NUEVO en la conversación" se aproxima con memoria por carga de página.
 */
const LOCAL_SEEN = new Map<string, Set<string>>();
function isNewForm(id: string, form: string): boolean {
  let seen = LOCAL_SEEN.get(id);
  if (!seen) {
    seen = new Set();
    LOCAL_SEEN.set(id, seen);
  }
  if (seen.has(form)) return false;
  seen.add(form);
  return true;
}
/** Solo para pruebas: olvida las formas ya vistas. */
export function resetLocalSeenForms(): void {
  LOCAL_SEEN.clear();
}

const CLITICS = "(?:(?:me|te|se|nous|vous|le|la|les|lui|leur|y|en)\\s+|[mtsl]')*";

const GERONDIF_NOT_VERB = new Set(['avant', 'tant', 'pendant', 'cependant', 'maintenant', 'durant']);
/** Gérondifs distintos del mensaje: "en travaillant", "tout en courant", "en le lisant". */
function detectGerondifs(text: string): string[] {
  const out = new Set<string>();
  const re = new RegExp(`\\ben\\s+${CLITICS}(\\p{L}{2,}ant)\\b`, 'gu');
  for (const m of text.matchAll(re)) if (!GERONDIF_NOT_VERB.has(m[1])) out.add(m[1]);
  return [...out];
}

const ETRE_FORMS =
  'suis|es|est|sommes|êtes|etes|sont|étais|etais|était|etait|étions|étiez|étaient|' +
  'serai|seras|sera|serons|serez|seront|serais|serait|serions|seriez|seraient|sois|soit|soyons|soyez|soient';
const FAIRE_FORMS =
  'fait|faite|faits|faites|fais|faisons|font|faire|ferai|feras|fera|ferons|ferez|feront|' +
  'ferais|ferait|ferions|feriez|feraient|faisais|faisait|faisions|faisiez|faisaient|fasse|fasses|fassent';
const ADVS =
  '(?:(?:pas|jamais|plus|rien|encore|déjà|deja|souvent|toujours|vraiment|bien|mieux|' +
  'sûrement|surement|certainement|beaucoup|trop|tout)\\s+)*';
// Palabras en -er/-ir/-re/-oir que NO son infinitivos tras "se faire".
const SE_FAIRE_NOT_INF = new Set([
  'plaisir', 'désir', 'desir', 'loisir', 'soir', 'noir', 'espoir', 'rare', 'libre', 'propre',
  'pauvre', 'autre', 'votre', 'notre', 'contre', 'entre', 'premier', 'dernier', 'hier', 'cher',
]);
/** «se faire + infinitif»: "je me suis fait voler", "s'est fait critiquer", "me faire opérer". */
function detectSeFaire(text: string): string[] {
  const out = new Set<string>();
  const re = new RegExp(
    `(?:\\b(?:me|te|se|nous|vous)\\s+|\\b[mts]')(?:(?:${ETRE_FORMS})\\s+)?${ADVS}(?:${FAIRE_FORMS})\\s+(\\p{L}{2,}(?:er|ir|re|oir))\\b`,
    'gu'
  );
  for (const m of text.matchAll(re)) if (!SE_FAIRE_NOT_INF.has(m[1])) out.add(m[1]);
  return [...out];
}

const COND_AVOIR = new Set(['aurais', 'aurait', 'aurions', 'auriez', 'auraient']);
// Con être solo cuentan los verbos que forman el pasado con être, o los
// pronominales ("se serait levé"): "serait content/fatigué" es conditionnel
// présent + adjetivo.
const ETRE_VERB_PP =
  /^(?:allé|venu|arrivé|parti|sorti|entré|rentré|resté|tombé|né|mort|monté|descendu|devenu|revenu|retourné|passé|parvenu|intervenu)(?:e|s|es)?$/u;
const PP_SHAPE = /(?:é|ée|és|ées|i|ie|is|ies|it|ite|its|ites|u|ue|us|ues|ert|erte|erts|ertes|int|inte|ints|intes|û)$/u;
const PP_IRREGULAR = new Set(['dû', 'pu', 'eu', 'su', 'vu', 'bu', 'lu', 'été', 'fait', 'dit', 'mis', 'pris']);
// Palabras con forma de participio que tras "aurait/serait" no lo son.
const PP_NOT = new Set([
  'envie', 'droit', 'petit', 'petite', 'petits', 'petites', 'plus', 'nous', 'vous', 'du', 'si', 'ni',
  'qui', 'lui', 'ici', 'aussi', 'ami', 'amie', 'amis', 'amies', 'mari', 'vie', 'pluie', 'tous', 'jus',
  'tu', 'besoin', 'minuit', 'midi', 'nuit', 'fruit', 'bruit', 'lit', 'crédit',
]);
/** Conditionnel passé: "j'aurais aimé", "on aurait pu", "elle serait venue", "il se serait levé". */
function detectConditionnelPasse(text: string): string[] {
  const out = new Set<string>();
  const re = new RegExp(
    `((?:\\b(?:me|te|se|nous|vous)\\s+|\\b[mts]')?)\\b(aurais|aurait|aurions|auriez|auraient|serais|serait|serions|seriez|seraient)\\s+${ADVS}(\\p{L}+)(?!\\p{L})`, // no \b final: es ASCII y corta "dû", "levé"
    'gu'
  );
  for (const m of text.matchAll(re)) {
    const [, clitic, aux, pp] = m;
    if (PP_NOT.has(pp)) continue;
    const ok = COND_AVOIR.has(aux)
      ? PP_SHAPE.test(pp) || PP_IRREGULAR.has(pp)
      : ETRE_VERB_PP.test(pp) || (clitic !== '' && PP_SHAPE.test(pp));
    if (ok) out.add(pp);
  }
  return [...out];
}

/* ── C: respaldo por regex para c_grammairien (desde el 11/09/2026) ──
 * Prueba E2E del 11/09: el LLM etiquetaba el discours indirect au passé como
 * "concordance" y nunca emitió "passive" (una pasiva la contó como conectores).
 * El trofeo SIGUE en el prompt (no es `local`): el LLM evalúa las 4 casillas y
 * esta regex solo asegura las tres formales. Tope 1 por casilla → sin doble
 * conteo si ambos coinciden.
 */
const HYBRID_TROPHIES = new Set(['c_grammairien']);

const REPORT_PP =
  'dit|annoncé|expliqué|raconté|écrit|répondu|promis|affirmé|déclaré|avoué|assuré|confié|' +
  'demandé|précisé|prévenu|juré|admis|reconnu|répété|expliqué';
const REPORT_PAST_SIMPLE =
  'disait|disaient|annonçait|expliquait|racontait|affirmait|déclarait|répondait|' +
  'pensait|pensais|croyait|croyais|savait|savais|annonça|expliqua|répondit|déclara|affirma|dirent';
const AVOIR_AUX = 'ai|as|a|avons|avez|ont|avais|avait|avions|aviez|avaient';
const IMPARFAIT_ETRE_AVOIR = 'avais|avait|avions|aviez|avaient|étais|était|étions|étiez|étaient';

function detectGrammairienSlots(text: string): Set<string> {
  const slots = new Set<string>();

  // subjonctif passé: "bien que Camus ait écrit", "surpris qu'il ait pris".
  const subj = new RegExp(
    `\\b(?:que|qu')\\s*(?:\\p{L}+\\s+){0,3}?(?:ne\\s+|n')?(aie|aies|ait|ayons|ayez|aient|sois|soit|soyons|soyez|soient)\\s+${ADVS}(\\p{L}+)(?!\\p{L})`,
    'gu'
  );
  for (const m of text.matchAll(subj)) {
    const [, aux, pp] = m;
    if (PP_NOT.has(pp)) continue;
    const avoir = aux.startsWith('a');
    if (avoir ? PP_SHAPE.test(pp) || PP_IRREGULAR.has(pp) : ETRE_VERB_PP.test(pp)) {
      slots.add('subj_passe');
      break;
    }
  }

  // discours indirect au passé: verbo de habla en pasado + que/si + un tiempo
  // "desplazado" (imparfait, plus-que-parfait o conditionnel) en la subordinada.
  const report = new RegExp(
    `(?:\\b(?:${AVOIR_AUX})\\s+(?:${REPORT_PP})|\\b(?:${REPORT_PAST_SIMPLE}))(?!\\p{L})\\s+(?:(?:à|a)\\s+(?:\\p{L}+\\s+){1,2})?(?:que|qu'|si|s')`,
    'gu'
  );
  const backshift = new RegExp(
    `(?:(?:^|[^\\p{L}])(?:${IMPARFAIT_ETRE_AVOIR})\\s+\\p{L}+|(?:^|[^\\p{L}])(\\p{L}{2,}(?:ais|ait|aient|ions|iez)))(?!\\p{L})`,
    'u'
  );
  for (const m of text.matchAll(report)) {
    const tail = text.slice((m.index ?? 0) + m[0].length).split(/[.!?;]/)[0];
    const b = tail.match(backshift);
    if (b && !(b[1] && (IMPARFAIT_PRESENT_AIS.has(b[1]) || IMPARFAIT_PRESENT_AIT.has(b[1])))) {
      slots.add('discours_passe');
      break;
    }
  }

  // passive en un relato pasado: "a été accueilli", "fut remportée",
  // "avait été arrêté", "était habité par". Con "était/étaient" se exige el
  // agente ("par") para no confundir con un adjetivo ("était fatigué").
  const passiveCompound = new RegExp(
    `\\b(?:(?:a|ont|avait|avaient)\\s+été|fut|furent)\\s+${ADVS}(\\p{L}+)(?!\\p{L})`,
    'gu'
  );
  for (const m of text.matchAll(passiveCompound)) {
    const pp = m[1];
    if (!PP_NOT.has(pp) && PP_SHAPE.test(pp) && !ETRE_VERB_PP.test(pp)) {
      slots.add('passive');
      break;
    }
  }
  if (!slots.has('passive')) {
    const passiveImp = new RegExp(
      `(?:^|[^\\p{L}])(?:était|étaient)\\s+${ADVS}(\\p{L}+)(?!\\p{L})(?:\\s+\\p{L}+){0,3}?\\s+par\\b`,
      'gu'
    );
    for (const m of text.matchAll(passiveImp)) {
      const pp = m[1];
      if (!PP_NOT.has(pp) && PP_SHAPE.test(pp) && !ETRE_VERB_PP.test(pp)) {
        slots.add('passive');
        break;
      }
    }
  }
  return slots;
}

/** Rangos del trofeo a1_compter para un texto ya normalizado. */
function detectNumberSlots(text: string): Set<string> {
  const slots = new Set<string>();

  // Cifras (STT suele transcribir números como dígitos): 1-3 cifras.
  for (const m of text.matchAll(/(?:^|[^\d])(\d{1,3})(?!\d)/g)) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 10) slots.add('n1_10');
    else if (n <= 69) slots.add('n11_69');
    else if (n <= 99) slots.add('n70_99');
    else slots.add('n100_999');
  }

  // Números en letras. Se detecta del rango MAYOR al menor y se RECORTA lo ya
  // reconocido, para que los compuestos no dupliquen ("soixante-quinze" es
  // 70-99, no debe dejar un "quinze" suelto; "deux cents" es 100-999, no debe
  // dejar un "deux"). Sin lookbehind: Safari viejo lo rompe al parsear.
  let t = text;
  const eat = (re: RegExp, slot: string): void => {
    if (!re.test(t)) return;
    slots.add(slot);
    t = t.replace(new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g'), ' ');
  };

  // 100-999: "cent(s)" con multiplicador y resto opcionales ("deux cent trente").
  eat(/\b(?:(?:deux|trois|quatre|cinq|six|sept|huit|neuf)[-\s])?cents?(?:[-\s](?:et[-\s])?[a-zà-ÿ-]+)?\b/g, 'n100_999');
  // 70-99: soixante-dix..seize, soixante et onze, quatre-vingt(s)(-…)
  eat(/\b(?:soixante[-\s](?:dix(?:[-\s][a-zà-ÿ]+)?|et[-\s]onze|douze|treize|quatorze|quinze|seize)|quatre[-\s]vingts?(?:[-\s](?:et[-\s])?[a-zà-ÿ]+)?)\b/g, 'n70_99');
  // 11-69: teens, dix-sept/huit/neuf y decenas con unidad opcional.
  eat(/\b(?:onze|douze|treize|quatorze|quinze|seize|dix[-\s](?:sept|huit|neuf)|(?:vingt|trente|quarante|cinquante|soixante)(?:[-\s](?:et[-\s])?(?:un|une|deux|trois|quatre|cinq|six|sept|huit|neuf))?)\b/g, 'n11_69');
  // 1-10: sin "un/une" (artículos indistinguibles del número).
  eat(/\b(?:deux|trois|quatre|cinq|six|sept|huit|neuf|dix)\b/g, 'n1_10');

  return slots;
}

/**
 * Clasifica las preguntas del mensaje en abiertas/cerradas (a1_questions).
 * Permisivo a propósito (feedback Santiago 26/08: el LLM sub-contaba), pero
 * exige señales de francés para no premiar preguntas en español.
 */
function detectQuestionSlots(text: string): string[] {
  const slots: string[] = [];
  const chunks = text.split('?').slice(0, -1); // cada trozo termina donde iba un '?'
  for (const raw of chunks) {
    // La pregunta es lo que sigue a la última puntuación fuerte del trozo.
    const q = ` ${raw.split(/[.!;]/).pop() || ''} `;
    if (q.includes('¿')) continue; // pregunta en español
    // Sin \b final: es ASCII y no ve el final de "où" (11/09/2026: "Où habites-tu ?"
    // se contaba como cerrada y "Où est la gare ?" no se contaba).
    if (/\b(?:pourquoi|comment|combien|quand|où|quel(?:le)?s?|qu'est[-\s]ce)(?!\p{L})/u.test(q)) {
      slots.push('ouverte');
    } else if (
      /\best[-\s]ce[-\s]qu/.test(q) ||
      /\b[a-zà-ÿ]+-(?:je|tu|il|elle|on|nous|vous|ils|elles)\b/.test(q) || // inversión: "as-tu", "aimes-tu"
      /\b(?:tu|vous|il|elle|on|ils|elles|c'est|ça)\b/.test(q) // entonación: "tu aimes le café ?"
    ) {
      slots.push('fermee');
    }
  }
  return slots;
}

/**
 * Reconocimientos DETERMINISTAS del último mensaje del estudiante, para los
 * trofeos `local: true` del nivel. Corre en el frontend en cada mensaje del
 * estudiante (App.tsx) y alimenta processRecognitions igual que los marcadores
 * del LLM — los topes por casilla evitan cualquier doble conteo.
 *
 * La corrección aquí ES la forma literal: "nous sommes" no puede estar mal
 * conjugado. Lo que exige juicio (relatos, passé composé, argumentación…)
 * sigue siendo del LLM.
 */
/** Solo para sondeos/pruebas. */
export function _detectPartitifSlotsForTests(message: string): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [k, v] of detectPartitifSlots(normalizeForDetection(message), message)) out[k] = [...v];
  return out;
}

export function detectLocalRecognitions(
  message: string,
  level?: MCERLevel | null
): Array<{ id: string; slot: string }> {
  if (!message || isKioskMode()) return []; // el kiosco tiene su propio respaldo
  const levelTrophies = trophiesForLevel(level);
  const active = new Set(levelTrophies.filter((t) => t.local).map((t) => t.id));
  // Trofeos que evalúa el LLM pero con respaldo por regex en algunas casillas.
  const hybrid = new Set(levelTrophies.filter((t) => HYBRID_TROPHIES.has(t.id)).map((t) => t.id));
  if (active.size === 0 && hybrid.size === 0) return [];

  const t = normalizeForDetection(message);
  const recs: Array<{ id: string; slot: string }> = [];
  const add = (id: string, slot: string) => {
    if (active.has(id)) recs.push({ id, slot });
  };

  // a1_saluer — un saludo por turno (variar saludos = varios turnos, spec D=5).
  if (/\b(?:bonjour|bonsoir|salut|coucou|bonne nuit)\b/.test(t)) add('a1_saluer', 'fois');

  // a1_etre — formas exactas por sujeto (tope 1 por casilla en el catálogo).
  if (/\bje\s+suis\b/.test(t)) add('a1_etre', 'je');
  if (/\btu\s+es\b/.test(t)) add('a1_etre', 'tu');
  if (/\b(?:il|elle|on)\s+est\b/.test(t)) add('a1_etre', 'il_elle');
  if (/\bnous\s+sommes\b/.test(t)) add('a1_etre', 'nous');
  if (/\bvous\s+(?:êtes|etes)\b/.test(t)) add('a1_etre', 'vous');
  if (/\b(?:ils|elles)\s+sont\b/.test(t)) add('a1_etre', 'ils_elles');

  // a1_localiser — mismas regexes que refineRecognitions (preposición literal).
  // La mayúscula del lugar se evalúa sobre el texto ORIGINAL (la normalización
  // a minúsculas la borraría).
  {
    const orig = ` ${message.replace(/[’‘]/g, "'")} `;
    if (/\s[àa]\s+[A-ZÀ-Ý]/u.test(orig)) add('a1_localiser', 'ville_a');
    if (/\ben\s+[A-ZÀ-Ý]/u.test(orig)) add('a1_localiser', 'pays_en');
    if (/\bau\s+[A-ZÀ-Ý]/u.test(orig)) add('a1_localiser', 'pays_au');
    if (/\baux\s+[A-ZÀ-Ý]/u.test(orig)) add('a1_localiser', 'pays_aux');
  }

  // a1_compter — por rango.
  for (const slot of detectNumberSlots(t)) add('a1_compter', slot);

  // a1_loisirs — expresiones exactas con algo después.
  if (/\bj'aime\s+\S/.test(t)) add('a1_loisirs', 'aime');
  if (/\bje\s+n'aime\s+pas\s+\S/.test(t)) add('a1_loisirs', 'aime_pas');
  if (/\bj'adore\s+\S/.test(t)) add('a1_loisirs', 'adore');
  if (/\bje\s+(?:déteste|deteste)\s+\S/.test(t)) add('a1_loisirs', 'deteste');

  // a1_famille — una mención por PALABRA de familia distinta en el mensaje.
  {
    const fam = t.match(/\b(?:mères?|meres?|maman|pères?|peres?|papa|frères?|freres?|sœurs?|soeurs?|cousins?|cousines?|parents|grand[-\s](?:mère|mere|père|pere)|grands[-\s]parents|oncles?|tantes?|fils|filles?|mari|femme|époux|épouse|neveux?|nièces?|nieces?|famille)\b/g);
    if (fam) for (const _ of new Set(fam)) add('a1_famille', 'mention');
  }

  // a1_questions — cada pregunta del mensaje, clasificada.
  for (const slot of detectQuestionSlots(t)) add('a1_questions', slot);

  // a2_imparfait — terminaciones regulares por sujeto; un reconocimiento por
  // verbo DISTINTO en el mensaje (la casilla admite 2 por sujeto).
  for (const [slot, verbs] of detectImparfaitSlots(t)) {
    for (const _ of verbs) add('a2_imparfait', slot);
  }

  // a2_pouvoir_vouloir — formas exactas, con negación/clíticos e inversión.
  for (const slot of detectPouvoirVouloirSlots(t)) add('a2_pouvoir_vouloir', slot);

  // a2_partitifs — un reconocimiento por sustantivo DISTINTO y casilla (la
  // casilla admite 2 por partitivo). La mayúscula de los nombres propios se
  // mira en el mensaje original.
  if (active.has('a2_partitifs')) {
    for (const [slot, nouns] of detectPartitifSlots(t, message)) {
      for (const _ of nouns) add('a2_partitifs', slot);
    }
  }

  // B2 — un reconocimiento por verbo DISTINTO y no visto antes en esta carga.
  const b2 = (id: string, forms: string[]) => {
    if (!active.has(id)) return;
    for (const f of forms) if (isNewForm(id, f)) add(id, 'fois');
  };
  b2('b2_gerondif', detectGerondifs(t));
  b2('b2_se_faire', detectSeFaire(t));
  b2('b2_conditionnel_passe', detectConditionnelPasse(t));

  // C — respaldo formal de c_grammairien (el LLM sigue evaluando las 4 casillas).
  if (hybrid.has('c_grammairien')) {
    for (const slot of detectGrammairienSlots(t)) recs.push({ id: 'c_grammairien', slot });
  }

  return recs;
}

/* ══════════════════ Bloque de prompt por nivel ══════════════════ */

/**
 * Construye el bloque <trophees> del prompt V3 para el nivel dado. Null si el
 * nivel no tiene catálogo. Determinista por nivel → seguro para prompt caching.
 */
export function buildTropheesPromptBlock(level: MCERLevel): string | null {
  // Los trofeos `local` NO van al prompt: los reconoce detectLocalRecognitions
  // por regex (más fiable y más barato). Con A1 completo en local, el nivel A1
  // ya no lleva bloque <trophees> — el prompt se acorta.
  const list = trophiesForLevel(level).filter((t) => !t.meta && t.rule && !t.local);
  if (list.length === 0) return null;
  const rules = list.map((t, i) => `${i + 1}. ${t.rule}`).join('\n');
  return `RECONOCIMIENTO DE LOGROS (interno — el estudiante NUNCA lo ve):
Al FINAL de tu respuesta, evalúa ÚNICAMENTE el último mensaje del estudiante. Si cumple alguno de los objetivos de abajo CON FRANCÉS CORRECTO, añade el/los marcadores correspondientes al final de tu texto, separados por espacios. El sistema los oculta y suma puntos por ti.
Reglas estrictas:
- Recorre los ${list.length} objetivos UNO POR UNO contra el último mensaje del estudiante: un mismo mensaje puede satisfacer varios objetivos Y varias casillas de un mismo objetivo — emite TODOS los marcadores que correspondan (máximo uno por casilla por turno, salvo que el objetivo diga otra cosa).
- Solo si la forma es CORRECTA (una conjugación errada, una preposición mal usada o una estructura equivocada NO cuentan; tampoco cuentan si tú se lo acabas de soplar y él solo lo repite idéntico).
- Solo las expresiones o estructuras EXACTAS que pide cada objetivo — parecidos no cuentan.
- JAMÁS menciones los marcadores, los trofeos ni este sistema al estudiante, y JAMÁS escribas un marcador en medio de una frase: siempre al final, después de tu última palabra.
Objetivos y marcadores:
${rules}
Los marcadores premian lo que dijo EL ESTUDIANTE, jamás tu propio texto.`;
}

/** Lista corta de objetivos del nivel (para el checklist final del prompt). */
export function tropheesChecklistLine(level: MCERLevel): string | null {
  const list = trophiesForLevel(level).filter((t) => !t.meta && t.rule && !t.local);
  if (list.length === 0) return null;
  const names = list.map((t) => t.title.toLowerCase()).join(', ');
  return `Bloque <trophees>: relee el último mensaje del estudiante contra los ${list.length} objetivos, UNO POR UNO (${names}), y confirma que emitiste un marcador por CADA casilla satisfecha.`;
}
